/**
 * The homepage.
 *
 * One job: get the visitor to a search. Everything below the fold exists to
 * suggest what is worth searching for, and to state plainly what this is —
 * an index of observations with sources, not a directory of adverts.
 *
 * The index counters are real. If the database holds nine businesses, it says
 * nine. Rounding up on a landing page is the first small lie a data product
 * tells, and it is the one that makes every later number suspect.
 */

import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { SearchBar } from '@/components/search/SearchBar'
import {
  ArrowRightIcon, PackageIcon, ScaleIcon,
  ShieldCheckIcon, SourceIcon, MapPinIcon,
} from '@/components/ui/icons'
import { cx, LinkButton, Panel, Skeleton } from '@/components/ui/primitives'
import { ChangePill, Sparkline, TickerTape, directionOf, toTickerItems }
  from '@/components/data/marks'
import { marketApi, priceApi, referenceApi, searchApi } from '@/services/endpoints'
import type { MarketRates } from '@/types/api'
import { formatMoney, formatNumber, formatRelative } from '@/utils/format'

const EXAMPLE_SEARCHES = [
  'plastic manufacturers in Anambra',
  'solar companies in Lagos',
  'rice wholesalers in Kano',
  'packaging suppliers',
  'logistics companies in Abuja',
  'cement prices',
]

export function HomePage() {
  const stats = useQuery({ queryKey: ['stats'], queryFn: searchApi.stats })
  const bootstrap = useQuery({ queryKey: ['bootstrap'], queryFn: referenceApi.bootstrap })
  const recent = useQuery({ queryKey: ['prices', 'recent'], queryFn: () => priceApi.recent(1) })
  const watch = useQuery({ queryKey: ['prices', 'movements'], queryFn: () => priceApi.movements(8) })
  const rates = useQuery({ queryKey: ['market-rates'], queryFn: marketApi.rates })

  return (
    <div>
      {/* -- Hero ---------------------------------------------------------- */}
      <section className="relative overflow-hidden border-b border-[var(--border-subtle)]">
        {/* A single soft wash of brand colour. Anything stronger would make a
            reference tool look like a campaign. */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.07]"
          style={{
            background:
              'radial-gradient(ellipse 70% 50% at 50% -10%, var(--color-brand-500), transparent)',
          }}
          aria-hidden="true"
        />

        <div className="relative mx-auto max-w-4xl px-4 pb-14 pt-16 text-center sm:px-6 sm:pt-24">
          <h1 className="text-3xl font-semibold tracking-tight text-[var(--text-primary)] sm:text-5xl">
            Find Nigerian businesses,
            <br className="hidden sm:block" /> products, services and prices.
          </h1>

          <p className="mx-auto mt-4 max-w-2xl text-base text-[var(--text-secondary)]">
            A structured index of who sells what, for how much, and where — built from
            published sources, checked for duplicates, and dated so you know how current
            it is.
          </p>

          <SearchBar
            size="lg"
            autoFocus
            className="mx-auto mt-8 max-w-2xl"
            placeholder="Search businesses, products, suppliers or services…"
          />

          <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
            <span className="text-xs text-[var(--text-tertiary)]">Try:</span>
            {EXAMPLE_SEARCHES.map((example) => (
              <Link
                key={example}
                to={`/search?q=${encodeURIComponent(example)}`}
                className="rounded-full border border-[var(--border-subtle)] bg-[var(--surface-panel)] px-3 py-1 text-xs text-[var(--text-secondary)] transition-colors hover:border-[var(--accent-border)] hover:text-[var(--accent-text)]"
              >
                {example}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* -- Markets ------------------------------------------------------
          Placed directly under the search, before the index counters. A
          visitor who came for a rate gets it without scrolling, and one who
          came to search has already passed the search box.

          Nothing here is only here: every figure repeats on /markets, which is
          what makes a moving element acceptable. */}
      {rates.data ? (
        <>
          <TickerTape items={toTickerItems(rates.data.indices, rates.data.fx)} />
          <MarketStrip rates={rates.data} />
        </>
      ) : null}

      {/* -- Index size ---------------------------------------------------- */}
      <section className="border-b border-[var(--border-subtle)] bg-[var(--surface-panel)]">
        <div className="mx-auto grid max-w-7xl grid-cols-2 lg:divide-x lg:divide-[var(--border-subtle)] px-4 sm:px-6 lg:grid-cols-5">
          <IndexStat
            label="Businesses"
            value={stats.data?.businesses}
            loading={stats.isLoading}
          />
          <IndexStat
            label="Verified"
            value={stats.data?.verified_businesses}
            loading={stats.isLoading}
            accent
          />
          <IndexStat
            label="Products"
            value={stats.data?.products}
            loading={stats.isLoading}
          />
          <IndexStat
            label="Current prices"
            value={stats.data?.prices}
            loading={stats.isLoading}
          />
          <IndexStat
            label="States covered"
            value={stats.data?.states_covered}
            suffix="/ 37"
            loading={stats.isLoading}
          />
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
        {/* -- Why this is different -------------------------------------- */}
        <section className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <ValueCard
            icon={<ScaleIcon size={17} />}
            title="Compare suppliers, not listings"
            body="One product, many sellers, side by side — with the unit and minimum order that make the numbers comparable."
          />
          <ValueCard
            icon={<SourceIcon size={17} />}
            title="See where it came from"
            body="Every price and claim links to the page it was read from, and the date it was last checked."
          />
          <ValueCard
            icon={<ShieldCheckIcon size={17} />}
            title="Know how much to rely on it"
            body="Completeness and corroboration are scored separately, so a full profile is never mistaken for a confirmed one."
          />
        </section>

        {/* -- Industries ------------------------------------------------- */}
        <section className="mt-14">
          <div className="flex items-end justify-between">
            <div>
              <h2 className="text-lg font-semibold text-[var(--text-primary)]">
                Browse by industry
              </h2>
              <p className="mt-0.5 text-sm text-[var(--text-secondary)]">
                Manufacturers, distributors, suppliers and service providers.
              </p>
            </div>
            <Link
              to="/search"
              className="hidden items-center gap-1 text-sm text-[var(--accent-text)] hover:underline sm:flex"
            >
              All industries <ArrowRightIcon size={14} />
            </Link>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {bootstrap.isLoading
              ? Array.from({ length: 8 }).map((_, index) => (
                  <Skeleton key={index} className="h-[4.5rem]" />
                ))
              : bootstrap.data?.featured_categories.map((category) => (
                  <Link
                    key={category.slug}
                    to={`/search?category=${category.slug}`}
                    className="group panel p-4 transition-colors hover:border-[var(--accent-border)]"
                  >
                    <p className="text-sm font-medium text-[var(--text-primary)] group-hover:text-[var(--accent-text)]">
                      {category.name}
                    </p>
                    <p className="mt-1 text-xs text-[var(--text-tertiary)] tabular">
                      {category.business_count > 0
                        ? `${formatNumber(category.business_count)} business${category.business_count === 1 ? '' : 'es'}`
                        : 'No entries yet'}
                    </p>
                  </Link>
                ))}
          </div>
        </section>

        {/* -- Price watch -------------------------------------------------- */}
        {/* What every price site in this market leads with, done honestly: a
            movement is two observations of the same offer — what we saw, when
            we saw it, both times — never a merchant's claimed discount. The
            section renders only once a price has actually been seen to change,
            because an empty "price drops" panel is a promise, and a fabricated
            one is the thing the competitors do that we will not. */}
        {watch.data && watch.data.movements.length > 0 ? (
          <section className="mt-14">
            <div className="flex items-end justify-between">
              <div>
                <h2 className="text-lg font-semibold text-[var(--text-primary)]">
                  Price movements
                </h2>
                <p className="mt-0.5 text-sm text-[var(--text-secondary)]">
                  Offers whose price changed between our observations.
                </p>
              </div>
              {watch.data.observed_last_24h > 0 ? (
                <p className="hidden text-xs text-[var(--text-tertiary)] sm:block">
                  {formatNumber(watch.data.observed_last_24h)} price
                  {watch.data.observed_last_24h === 1 ? '' : 's'} checked in the last 24h
                </p>
              ) : null}
            </div>
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {watch.data.movements.slice(0, 8).map((move, index) => (
                <Panel
                  key={`${move.product?.slug}-${move.business?.slug}-${index}`}
                  className="flex flex-col gap-1.5"
                >
                  <Link
                    to={move.product ? `/products/general/${move.product.slug}` : '/products'}
                    className="truncate text-sm font-medium text-[var(--text-primary)] hover:text-[var(--accent-text)]"
                  >
                    {move.product?.name ?? 'Unknown product'}
                  </Link>
                  <div className="flex items-baseline gap-2">
                    <span className="text-base font-semibold tabular text-[var(--text-primary)]">
                      {formatMoney(move.current, move.currency)}
                    </span>
                    <span
                      className={cx(
                        'text-xs font-semibold tabular',
                        move.direction === 'down'
                          ? 'text-[var(--chart-gain)]'
                          : 'text-[var(--chart-loss)]',
                      )}
                    >
                      {move.direction === 'down' ? '\u25bc' : '\u25b2'}
                      {move.percent ? ` ${move.percent.replace('-', '')}%` : ''}
                    </span>
                  </div>
                  <p className="text-2xs text-[var(--text-tertiary)]">
                    was {formatMoney(move.previous, move.currency)}
                    {move.previous_observed_at
                      ? ` ${formatRelative(move.previous_observed_at)}`
                      : ''}
                    {move.business ? ` \u00b7 ${move.business.name}` : ''}
                  </p>
                </Panel>
              ))}
            </div>
          </section>
        ) : null}

        {/* -- Recently observed prices ------------------------------------ */}
        <section className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="flex items-end justify-between">
              <div>
                <h2 className="text-lg font-semibold text-[var(--text-primary)]">
                  Recently observed prices
                </h2>
                <p className="mt-0.5 text-sm text-[var(--text-secondary)]">
                  The freshest entries in the index.
                </p>
              </div>
              <Link
                to="/products"
                className="hidden items-center gap-1 text-sm text-[var(--accent-text)] hover:underline sm:flex"
              >
                All products <ArrowRightIcon size={14} />
              </Link>
            </div>

            <Panel padded={false} className="mt-4 overflow-hidden">
              {recent.isLoading ? (
                <div className="space-y-3 p-4">
                  {Array.from({ length: 5 }).map((_, index) => (
                    <Skeleton key={index} className="h-9" />
                  ))}
                </div>
              ) : recent.data && recent.data.data.length > 0 ? (
                <ul className="divide-hairline">
                  {recent.data.data.slice(0, 7).map((offer, index) => (
                    <li
                      key={`${offer.product?.slug}-${offer.business?.uuid}-${index}`}
                      className="flex items-center gap-3 px-4 py-2.5 transition-colors hover:bg-[var(--surface-hover)]"
                    >
                      <div className="min-w-0 flex-1">
                        <Link
                          to={offer.product ? `/products/general/${offer.product.slug}` : '/products'}
                          className="block truncate text-sm font-medium text-[var(--text-primary)] hover:text-[var(--accent-text)]"
                        >
                          {offer.product?.name ?? 'Unknown product'}
                        </Link>
                        <p className="truncate text-xs text-[var(--text-tertiary)]">
                          {offer.business?.name}
                          {offer.business?.location ? ` · ${offer.business.location}` : ''}
                        </p>
                      </div>
                      <div className="shrink-0 text-right">
                        <p className="text-sm font-semibold tabular text-[var(--text-primary)]">
                          {offer.price_type === 'quote'
                            ? 'On request'
                            : formatMoney(offer.amount, offer.currency ?? 'NGN')}
                        </p>
                        <p className="text-2xs text-[var(--text-tertiary)]">
                          {formatRelative(offer.observed_at)}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="px-4 py-10 text-center">
                  <PackageIcon size={20} className="mx-auto text-[var(--text-tertiary)]" />
                  <p className="mt-2 text-sm text-[var(--text-secondary)]">
                    No prices published yet.
                  </p>
                  <p className="mt-1 text-xs text-[var(--text-tertiary)]">
                    Prices appear once a source has been crawled and reviewed.
                  </p>
                </div>
              )}
            </Panel>
          </div>

          {/* -- Method ---------------------------------------------------- */}
          <aside>
            <h2 className="text-lg font-semibold text-[var(--text-primary)]">
              How this is built
            </h2>
            <ol className="mt-4 space-y-3">
              {[
                { step: 'Discover', text: 'Business websites, marketplaces and registries are located and queued.' },
                { step: 'Extract', text: 'Structured data first, page content second, AI only where neither suffices.' },
                { step: 'Resolve', text: 'The same company under five names becomes one record — never on a name match alone.' },
                { step: 'Review', text: 'Anything uncertain goes to a person before it is published.' },
              ].map((item, index) => (
                <li key={item.step} className="flex gap-3">
                  <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-[var(--accent-subtle)] text-2xs font-semibold text-[var(--accent-text)] tabular">
                    {index + 1}
                  </span>
                  <div>
                    <p className="text-sm font-medium text-[var(--text-primary)]">{item.step}</p>
                    <p className="mt-0.5 text-xs leading-relaxed text-[var(--text-secondary)]">
                      {item.text}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
            <LinkButton to="/methodology" variant="secondary" size="sm" className="mt-5 w-full">
              Read the methodology
            </LinkButton>
          </aside>
        </section>

        {/* -- Geography -------------------------------------------------- */}
        <section className="mt-14">
          <Panel className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
            <div className="flex items-start gap-3">
              <MapPinIcon size={20} className="mt-0.5 shrink-0 text-[var(--accent)]" />
              <div>
                <h2 className="text-sm font-semibold text-[var(--text-primary)]">
                  Search down to the local government area
                </h2>
                <p className="mt-0.5 text-sm text-[var(--text-secondary)]">
                  A buyer in Onitsha does not want a supplier “in Anambra”. All 36 states,
                  the FCT and 774 LGAs are indexed.
                </p>
              </div>
            </div>
            <LinkButton to="/states" variant="secondary" size="sm" className="shrink-0">
              Browse by state
            </LinkButton>
          </Panel>
        </section>
      </div>
    </div>
  )
}

/* -- Pieces ---------------------------------------------------------------- */
function IndexStat({
  label, value, suffix, loading, accent,
}: {
  label: string
  value?: number
  suffix?: string
  loading?: boolean
  accent?: boolean
}) {
  return (
    <div className="px-4 py-5 text-center">
      {loading ? (
        <Skeleton className="mx-auto h-7 w-16" />
      ) : (
        <p
          className={cx(
            'text-xl font-semibold tabular sm:text-2xl',
            accent ? 'text-[var(--accent-text)]' : 'text-[var(--text-primary)]',
          )}
        >
          {formatNumber(value ?? 0)}
          {suffix ? (
            <span className="ml-1 text-sm font-normal text-[var(--text-tertiary)]">
              {suffix}
            </span>
          ) : null}
        </p>
      )}
      <p className="mt-1 field-label">{label}</p>
    </div>
  )
}

function ValueCard({
  icon, title, body,
}: {
  icon: React.ReactNode
  title: string
  body: string
}) {
  return (
    <Panel>
      <div className="flex size-9 items-center justify-center rounded-lg bg-[var(--accent-subtle)] text-[var(--accent-text)]">
        {icon}
      </div>
      <h3 className="mt-3 text-sm font-semibold text-[var(--text-primary)]">{title}</h3>
      <p className="mt-1.5 text-sm leading-relaxed text-[var(--text-secondary)]">{body}</p>
    </Panel>
  )
}


/**
 * A four-tile market summary for the homepage.
 *
 * Deliberately not the whole board. The homepage's job is still to get a
 * visitor to a search, so this shows the naira, two world markets and the
 * index's own benchmark — enough to signal that the data is live and current,
 * with a link for anyone who wants the rest.
 *
 * Each tile states when its source published, because "live" and "yesterday's
 * close" are different claims and only one of them is true here.
 */
function MarketStrip({ rates }: { rates: MarketRates }) {
  const usd = rates.fx.find((rate) => rate.symbol.startsWith('USD/'))
  const markets = rates.indices.slice(0, 2)
  const benchmark = rates.index_benchmarks[0]

  const tiles: Array<{
    key: string
    label: string
    value: string
    changePercent: string | null
    footnote: string
    history: MarketRates['fx'][number]['history']
  }> = []

  if (usd) {
    tiles.push({
      key: usd.symbol,
      label: 'US dollar',
      value: `₦${Number(usd.amount).toLocaleString(undefined, {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
      })}`,
      changePercent: usd.change_percent,
      footnote: `Published ${formatRelative(usd.observed_at)}`,
      history: usd.history,
    })
  }

  for (const market of markets) {
    tiles.push({
      key: market.symbol,
      label: market.name,
      value: Number(market.value).toLocaleString(undefined, {
        maximumFractionDigits: 2,
      }),
      changePercent: market.change_percent,
      footnote: `${market.country_code} · session close`,
      history: market.history,
    })
  }

  if (benchmark) {
    tiles.push({
      key: benchmark.symbol,
      label: benchmark.name,
      value: `₦${Number(benchmark.amount).toLocaleString(undefined, {
        maximumFractionDigits: 0,
      })}`,
      changePercent: benchmark.change_percent,
      footnote: `Median of ${formatNumber(benchmark.observation_count)} offers`,
      history: benchmark.history,
    })
  }

  if (tiles.length === 0) return null

  return (
    <section className="border-b border-[var(--border-subtle)] bg-[var(--surface-page)]">
      <div className="mx-auto max-w-7xl px-4 py-5 sm:px-6">
        <div className="mb-3 flex items-baseline justify-between">
          <h2 className="field-label text-[var(--text-tertiary)]">Markets</h2>
          <Link
            to="/markets"
            className="inline-flex items-center gap-1 text-xs text-[var(--text-secondary)] hover:text-[var(--accent-text)]"
          >
            All market data
            <ArrowRightIcon size={11} />
          </Link>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {tiles.map((tile) => (
            <Panel key={tile.key} className="flex flex-col gap-1">
              <div className="flex items-center justify-between gap-2">
                <span className="truncate text-xs font-medium text-[var(--text-secondary)]">
                  {tile.label}
                </span>
                <ChangePill changePercent={tile.changePercent} />
              </div>
              <div className="flex items-end justify-between gap-2">
                <span className="text-xl font-semibold text-[var(--text-primary)]">
                  {tile.value}
                </span>
                <Sparkline
                  points={tile.history}
                  direction={directionOf(tile.changePercent)}
                  width={64}
                  height={24}
                />
              </div>
              <span className="text-[0.6rem] text-[var(--text-tertiary)]">
                {tile.footnote}
              </span>
            </Panel>
          ))}
        </div>
      </div>
    </section>
  )
}
